import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Input } from "antd"
import type { TableColumnsType } from "antd"
import { Button, DashboardContainer } from "components"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useState } from "react"
import { Link } from "react-router-dom"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"
import {
  ADMIN_PEOPLE_ENDPOINTS,
  adminPeopleService,
} from "services/adminPeopleService"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import { AppTable, TableActionSelect } from "components/new-stuff/app-table"

interface ExitData {
  person_id: string
  person_key: string
  first_name: string
  middle_name: string
  last_name: string
  avatar_url: string
  department: string
  exit_type: string
  exit_date: string
}

interface IExitsPage {
  docs: ExitData[]
  page: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPage: number | null
  previousPage: number | null
  limit: number
  totalDocs: number
  totalPages: number
}

const Exits = () => {
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const [activeRehireId, setActiveRehireId] = useState("")
  const [openRehireModal, setOpenRehireModal] = useState(false)

  const {
    isLoading: isLoadingExits,
    data: exitData,
    search,
    handleSearchChange,
    tablePagination,
  } = useBackendTableQuery<IBaseRes<IExitsPage>>({
    queryKeyBase: [ADMIN_PEOPLE_ENDPOINTS.SELECT_EXITS],
    queryFn: (params) => adminPeopleService.selectExits(params),
    getTotal: (data) => data?.data?.totalDocs ?? 0,
    searchParamName: "searchParams",
    initialPageSize: 20,
  })

  const { isLoading: isLoadingRehire, mutate: submitRehire } = useMutation({
    mutationFn: (): Promise<IBaseRes<null>> =>
      adminPeopleService.rehireEmployee(activeRehireId),
    onSuccess: (data) => {
      setOpenRehireModal(false)
      queryClient.invalidateQueries({
        queryKey: [ADMIN_PEOPLE_ENDPOINTS.SELECT_EXITS],
      })
      data?.message && toast.success(data.message)
    },
    onError: (err) => {
      setOpenRehireModal(false)
      onQueryError(err)
    },
  })

  const TableColumns: TableColumnsType<ExitData> = [
    {
      title: "Employee",
      dataIndex: "first_name",
      render: (_, record) => (
        <div className="flex items-center gap-2 text-[#42526D] text-sm">
          <span className="shrink-0">
            <PersonAvatar
              className="w-8 h-8 rounded-full"
              src={record.avatar_url}
              alt=""
            />
          </span>

          {record.first_name ? (
            <span className="text-[#42526D] capitalize text-sm">
              {record.first_name}
              {record.middle_name ? ` ${record.middle_name} ` : " "}
              {record.last_name}
            </span>
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (department: string) => (
        <span className="whitespace-nowrap text-sm text-[#42526D]">
          {department}
        </span>
      ),
    },
    {
      title: "Exit type",
      dataIndex: "exit_type",
      render: (_, record) => (
        <div className="flex flex-col ">
          <span className="whitespace-nowrap text-sm text-[#42526D]">
            {record.exit_type}
          </span>
          <span className="text-xs text-neutral300 font-thin">
            {record.exit_date}
          </span>
        </div>
      ),
    },
    {
      title: "",
      key: "action",
      render: (_, record) => <TableActionSelect items={actionItems(record)} />,
    },
  ]
  //  three dots on each row
  const actionItems = (record: ExitData) => [
    {
      key: "1",
      label: (
        <button
          onClick={() => {
            setOpenRehireModal(true)
            setActiveRehireId(String(record.person_id))
          }}
          className="w-full text-left text-neutral500"
        >
          Rehire
        </button>
      ),
    },
    {
      key: "2",
      label: (
        <Link
          className="w-full text-left text-neutral500"
          to={`/dashboard/employee/${record.person_key}/job-and-pay`}
        >
          View
        </Link>
      ),
    },
  ]

  return (
    <div className="relative text-[#42526D]">
      <DashboardContainer>
        <div className="flex flex-col h-full">
          <div className="bg-white  flex items-center justify-between gap-4 py-[10px] px-4">
            <div className="w-full  flex items-center gap-4">
              <div className="max-w-[200px]">
                <Input
                  placeholder="Search"
                  prefix={
                    <MagnifyingGlassIcon className="w-4 h-4 text-neutral-300" />
                  }
                  value={search}
                  onChange={(e) => {
                    handleSearchChange(e.target.value)
                  }}
                />
              </div>
            </div>
          </div>
          <AppTable
            columns={TableColumns}
            dataSource={exitData?.data?.docs || []}
            rowKey="person_id"
            loading={isLoadingExits}
            className="text-[#42526D]"
            pagination={tablePagination}
          />
        </div>
      </DashboardContainer>

      <CustomModal
        title="Rehire employee"
        onCancel={() => {
          setOpenRehireModal(false)
        }}
        open={openRehireModal}
        width={500}
        footer={
          <div className="flex justify-end gap-4">
            <Button
              color="neutral"
              onClick={() => {
                setOpenRehireModal(false)
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className=""
              onClick={() => {
                submitRehire()
              }}
              disabled={isLoadingRehire}
              submitting={isLoadingRehire}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <div className=" flex flex-col gap-4">
          <p className="text-[#15294B] text-sm mb-6 ">
            If you rehire this employee, they’ll be able to partake in all
            company activities and will be included in future payroll. Click
            Confirm to continue.
          </p>
        </div>
      </CustomModal>
    </div>
  )
}
export default Exits
